import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

interface InfoHintProps {
  content: ReactNode;
  /** Barrierefreies Label des Auslöse-Buttons. Default: „Mehr Informationen". */
  label?: string;
  /** Öffnungsrichtung des Popovers. 'top' für Auslöser am unteren Bildschirmrand (z.B. Composer). */
  side?: 'top' | 'bottom';
  className?: string;
}

/**
 * Kleines Info-Icon als Button, das per Popover den `content` zeigt. Öffnet bei
 * Hover sowie bei Klick/Fokus (Tastatur), schließt bei Esc oder Klick außerhalb.
 * Genutzt z.B. für den 24h-Hinweis oder den Character-Counter.
 */
export function InfoHint({
  content,
  label = 'Mehr Informationen',
  side = 'bottom',
  className = '',
}: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const id = useId();
  const rootRef = useRef<HTMLSpanElement | null>(null);

  const visible = open || hover;

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className={['relative inline-flex', className].join(' ')}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={visible ? id : undefined}
        onClick={() => setOpen((o) => !o)}
        className={[
          'inline-flex h-5 w-5 items-center justify-center rounded-pill text-kalkuel-muted transition-colors',
          'hover:bg-kalkuel-accent-soft hover:text-kalkuel-accent-strong',
          'focus-visible:outline-none focus-visible:shadow-focus-accent',
          open ? 'bg-kalkuel-accent-soft text-kalkuel-accent-strong' : '',
        ].join(' ')}
      >
        <Info size={14} />
      </button>
      {visible && (
        <span
          id={id}
          role="tooltip"
          className={[
            'absolute left-1/2 z-[80] -translate-x-1/2 animate-fade-in',
            side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
            'w-max max-w-xs whitespace-normal rounded-field border border-kalkuel-border bg-white px-3 py-2',
            'text-[11px] font-medium leading-snug text-kalkuel-ink shadow-pop',
          ].join(' ')}
        >
          {content}
        </span>
      )}
    </span>
  );
}
